import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  Save, Search, Tag, Package, Box, Settings, List, 
  Trash2, ArrowLeft, Info, Plus, X, ChevronRight, Paperclip, ChevronLeft,
  History as HistoryIcon, ShieldCheck, User, Calendar, GitBranch, RefreshCw, Type, Minus
} from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة

// استيراد المودالز
import UniversalLookup from './UniversalLookup';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import AddToolToStoreModal from './AddToolToStoreModal';
import ClassificationTree from './ClassificationTree';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext'; // 🎯 التحكم في الخط

const ToolDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const { canCreate, canUpdate, canDelete } = usePermissions('tools');

  const [activeTab, setActiveTab] = useState('TOOL');
  const [fieldMaps, setFieldMaps] = useState({});
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0); 
  const [selectedSpecIdx, setSelectedSpecIdx] = useState(null);
  const [showClassTree, setShowClassTree] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [isAddStoreModalOpen, setIsAddStoreModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  const [data, setData] = useState({
    toolnum: '', description: '', status: 'ACTIVE', tooltype: 'HAND',
    is_stocked: false, rotating: true, capitalized: false,
    orderunit: 'EACH', issueunit: 'EACH', calibration_required: false,
    inventory: [], specifications: [], qualifications: [], vendors: []
  });

  const [nav, setNav] = useState({ current_pos: 0, total: 0, next: null, prev: null, first: null, last: null });
  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', domainId: '', field: '', filters: {} });
  
  // --- التحكم في الفونت ---
  const FontControls = () => (
    <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
      <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
      <div className="flex items-center gap-1 min-w-[40px] justify-center text-blue-200">
        <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
      </div>
      <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
    </div>
  );

  const loadData = useCallback(async () => {
    try {
        const [assetMaps, meterMaps] = await Promise.all([
            axios.get('/api/admin/field-map/tools'),
            axios.get('/api/admin/field-map/Meters')
        ]);
        setFieldMaps({ ...(assetMaps.data || {}), ...(meterMaps.data || {}) });
    }
    catch (err) { toast.error(t('common.load_error', "Error loading field maps")); }
    
    if (isNew) return;
    
    setLoading(true);
    try {
      const res = await axios.get(`/api/tools/${id}`);
      setData(res.data);
      
      // جلب الملحقات
      const countRes = await axios.get(`/api/attachments/count/TOOL/${id}`);
      setCount(countRes.data.count || 0);
    } catch (err) { toast.error(t('common.load_error', "Error loading tool")); }
    finally { setLoading(false); }
  }, [id, isNew, t]);

  useEffect(() => { loadData(); }, [loadData]);

  // 🎯 جلب النافيجيشن بناءً على الكويري
  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const navRes = await axios.get(`/api/tools/${id}/navigation${location.search}`);
      setNav(navRes.data);
    } catch (err) { console.error("Nav Load Failed"); }
  }, [id, isNew, location.search]);
  
  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  const goTo = (targetId) => targetId && navigate(`/tools/${targetId}${location.search}`);

  const handleClassSelect = async (node) => {
    try {
      const loadId = toast.loading(t('common.loading', "Fetching attributes..."));
      const res = await axios.get(`/api/classifications/${node.classstructureid}/attributes`);
      
      const newSpecs = res.data.map(attr => ({
        assetattrid: attr.assetattrid, description: attr.description, datatype: attr.datatype,
        uom: attr.uom, domainid: attr.domainid, alnvalue: '', numvalue: null
      }));

      setData(prev => ({
        ...prev, classstructureid: node.classstructureid, classstructureid_description: node.description, specifications: newSpecs
      }));

      toast.success(t('tools.messages.class_loaded', "Classification Loaded"), { id: loadId });
      setShowClassTree(false);
    } catch (err) { toast.error(t('common.error', "Failed to load attributes")); }
  };

  const handleSave = async () => {
    if (!data.toolnum || !data.description) return toast.error(t('tools.messages.req_fields', "Tool Number and Description are required!"));
    const lid = toast.loading(t('common.saving', "Saving Tool Master..."));
    try {
      await axios.post('/api/tools/save', data);
      toast.success(t('common.save_success', "Tool Saved Successfully"), { id: lid });
      if (isNew) navigate(`/tools/${data.toolnum}${location.search}`);
    } catch (err) { toast.error(t('common.save_fail', "Save Failed"), { id: lid }); }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('tools.messages.delete_warn', "You won't be able to revert this!"),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
      confirmButtonText: t('common.yes_delete', 'Yes, delete tool!'),
      customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
    });
    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/tools/${id}`);
        toast.success(t('common.delete_success', "Tool Deleted"));
        navigate(`/tools${location.search}`);
      } catch (err) { toast.error(t('common.delete_fail', "Delete Failed")); }
    }
  };

  const handleSmartSearch = (fieldName, title, defaultType) => {
    const linkedDomain = fieldMaps[fieldName];
    let extraFilters = {};
    if (fieldName === 'siteid' && data.orgid) extraFilters = { org_id: data.orgid };
    
    setLookup({ 
        isOpen: true, title: title, type: linkedDomain ? 'DOMAIN' : defaultType, 
        domainId: linkedDomain || '', field: fieldName, filters: extraFilters
    });
  };

  useEffect(() => {
    const openStatusModal = () => { setShowStatusModal(true); };
    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    return () => window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
  }, []);

  useEffect(() => {
    const AddModalOpen = () => { setIsAddModalOpen(true); };
    window.addEventListener('OPEN_ADD_ITEM_STOREROOM', AddModalOpen);
    return () => window.removeEventListener('OPEN_ADD_ITEM_STOREROOM', AddModalOpen);
  }, []);

  useEffect(() => {
    const triggerSave = () => handleSave();
    window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
  }, [handleSave]);
  
  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);
  
  useEffect(() => {
    const handleOpenHistory = () => { setShowHistoryModal(true); };
    window.addEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    return () => { window.removeEventListener('OPEN_STATUS_HISTORY', handleOpenHistory); };
  }, []);

  const handleInputChange = (f, v) => setData(prev => ({ ...prev, [f]: v }));

  const addNewSpecification = () => {
    const newSpec = { assetattrid: '', description: '', datatype: 'ALN', alnvalue: '', numvalue: null, uom: '', domainid: null };
    setData({ ...data, specifications: [...(data.specifications || []), newSpec] });
    setSelectedSpecIdx((data.specifications || []).length);
  };

  const handleSpecUpdate = (field, value) => {
    if (selectedSpecIdx === null) return;
    const newList = [...data.specifications];
    newList[selectedSpecIdx][field] = value;
    setData({ ...data, specifications: newList });
  };

  if (loading) return <div className="p-20 text-center animate-pulse font-black text-[#005495]">{t('common.loading', 'Loading...')}</div>;

  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] uppercase tracking-tighter overflow-hidden duration-300 transition-[font-size] ${isRTL ? 'font-cairo text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* --- TOP TOOLBAR --- */}
      <div className="bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/tools${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full text-blue-200 transition-colors">
            {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18}/>}
          </button>
          
          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 `}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1"><span className="text-orange-400 font-black">{nav.current_pos}</span> / {nav.total}</div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>
        
        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          {((isNew && canCreate) || (!isNew && canUpdate)) && (
            <button onClick={handleSave} className="bg-green-600 hover:bg-green-700 text-white px-6 py-1.5 rounded-sm flex items-center gap-2 font-black transition-all shadow-md">
              <Save size={14} /> {t('common.save', 'SAVE')}
            </button>
          )}
          
          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
          
          {!isNew && (
            <button onClick={() => setShowStatusModal(true)} className="p-2 hover:bg-white/10 rounded text-blue-200" title={t('common.change_status')}><Tag size={16}/></button>
          )}
          {!isNew && (
            <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200">
              <Paperclip size={16} />
              {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{count}</span>}
            </button>
          )}
          {!isNew && (
            <button onClick={() => setShowHistoryModal(true)} className="p-2 hover:bg-white/10 rounded text-blue-200"><HistoryIcon size={16}/></button>
          )}
          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 hover:bg-red-500/20 rounded text-red-400"><Trash2 size={16}/></button>
          )}
          <FontControls />
        </div>
      </div>

      {/* --- HEADER INFO --- */}
      <div className="bg-white border-b px-6 py-3 flex items-center gap-12 shadow-sm z-20">
        <div className="flex flex-col min-w-[150px]">
            <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest">{t('tools.fields.toolnum', 'Tool Number')}</label>
            <input 
              className={`text-sm font-black text-[#1a3353] outline-none border-b-2 border-transparent focus:border-orange-500 bg-transparent transition-all ${isRTL ? 'text-right' : ''}`}
              value={data.toolnum}
              placeholder={t('tools.placeholders.toolid', 'ENTER TOOL ID...')}
              disabled={!isNew}
              onChange={e => handleInputChange('toolnum', e.target.value.toUpperCase())} 
            />
        </div>
        <div className={`flex flex-col flex-1 border-gray-200 ${isRTL ? 'border-r pr-8' : 'border-l pl-8'}`}>
          <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest">{t('assets.headers.desc', 'Description')}</label>
          <input className={`text-sm font-bold text-gray-700 outline-none w-full bg-transparent focus:text-blue-600 border-b border-transparent focus:border-blue-500 transition-all ${isRTL ? 'text-right' : ''}`} value={data.description} onChange={e => handleInputChange('description', e.target.value)} />
        </div>
        <div className={`flex flex-col border-gray-200 ${isRTL ? 'border-r pr-8 text-right' : 'border-l pl-8'}`}>
          <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest">{t('assets.headers.status', 'Status')}</label>
          <span className={`px-3 py-1 rounded-full font-black text-[10px] w-fit mt-1 ${data.status === 'ACTIVE' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{data.status}</span>
        </div>
      </div>

      {/* --- TABS --- */}
      <div className="flex bg-[#1a3353] text-white shadow-md px-4 overflow-x-auto no-scrollbar">
        {[
          { id: 'TOOL', label: t('tools.tabs.tool', 'TOOL'), icon: <Settings size={14}/> },
          { id: 'STORES', label: t('tools.tabs.stores', 'STORES'), icon: <Package size={14}/> },
          { id: 'VENDORS', label: t('tools.tabs.vendors', 'VENDORS'), icon: <Box size={14}/> },
          { id: 'SPECS', label: t('tools.tabs.specs', 'SPECS'), icon: <List size={14}/> },
          { id: 'QUALS', label: t('tools.tabs.quals', 'QUALS'), icon: <ShieldCheck size={14}/> }
        ].map(tb => (
          <button key={tb.id} onClick={() => setActiveTab(tb.id)} 
            className={`px-8 py-3 flex items-center gap-2 font-black border-white/5 transition-all whitespace-nowrap ${isRTL ? 'border-l' : 'border-r'} ${activeTab === tb.id ? 'bg-white text-[#1a3353]' : 'hover:bg-white/10 opacity-75'}`}>
            {tb.icon} {tb.label}
          </button>
        ))}
      </div>

      {/* --- CONTENT --- */}
      <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-[#f8f9fb]">
        {activeTab === 'TOOL' && (
          <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in duration-500">
            <SectionCard title={t('tools.sections.config', 'Tool Configuration')}>
               <div className="grid grid-cols-2 gap-20">
                  <div className="space-y-5">
                    <EditableField label={t('tools.fields.type', 'Tool Type')} value={data.tooltype} onChange={v => handleInputChange('tooltype', v)} onSearch={() => {handleSmartSearch('tooltype', t('tools.lookup.type', 'Tool Type'), 'DOMAIN')}} isRTL={isRTL} />
                    <DataField label={t('tools.fields.issueunit', 'Issue Unit')} value={data.issueunit} onSearch={() => {handleSmartSearch('issueunit', t('tools.lookup.unit', 'Issue Units'), 'DOMAIN')}} isRTL={isRTL} />
                    <div className="pt-4 space-y-3 border-t border-gray-100">
                      <CheckboxField label={t('tools.fields.is_stocked', 'Is Stocked Tool?')} checked={data.is_stocked} onChange={v => handleInputChange('is_stocked', v)} color="orange" isRTL={isRTL} />
                      <CheckboxField label={t('tools.fields.rotating', 'Rotating Item?')} checked={data.rotating} onChange={v => handleInputChange('rotating', v)} isRTL={isRTL} />
                      <CheckboxField label={t('tools.fields.calib_req', 'Calibration Required?')} checked={data.calibration_required} onChange={v => handleInputChange('calibration_required', v)} isRTL={isRTL} />
                    </div>
                  </div>
                  <div className={`space-y-5 border-gray-100 ${isRTL ? 'border-r pr-10' : 'border-l pl-10'}`}>
                     <StaticField label={t('tools.fields.mfg', 'Manufacturer')} value={data.manufacturer} />
                     <StaticField label={t('tools.fields.model', 'Model Num')} value={data.model_num} />
                     <div className={`bg-orange-50 p-6 border-orange-500 rounded-sm shadow-inner italic font-bold text-orange-900 leading-relaxed uppercase mt-6 ${isRTL ? 'border-r-4' : 'border-l-4'}`}>
                        {t('tools.messages.stocked_note', 'Note: Stocked tools are managed through storerooms and require an inventory record to track balances.')}
                     </div>
                  </div>
               </div>
            </SectionCard>
          </div>
        )}

        {activeTab === 'STORES' && (
          <div className="max-w-6xl mx-auto bg-white border shadow-xl rounded-sm overflow-hidden animate-in slide-in-from-right">
             <div className="bg-gray-50 p-3 border-b flex justify-between px-8 items-center">
                <span className="font-black text-blue-900 tracking-widest italic text-[10px]">{t('tools.sections.inventory', 'Inventory & Stock Balances')}</span>
                <button onClick={() => setIsAddModalOpen(true)} className="bg-blue-600 text-white px-4 py-1.5 rounded-sm font-black text-[9px] hover:bg-blue-800 shadow-md flex items-center gap-2 transition-transform active:scale-95">
                   <Plus size={12}/> {t('tools.actions.add_store', 'Add to Storeroom')}
                </button>
             </div>
             <InventoryTable inventory={data.inventory} isRTL={isRTL} t={t} />
          </div>
        )}
        {activeTab === 'VENDORS' && (
            <div className="bg-white border shadow-2xl max-w-6xl mx-auto rounded-sm overflow-hidden animate-in slide-in-from-right duration-500">
                <VendorsTable vendors={data.vendors} isRTL={isRTL} t={t} />
            </div>
        )}
        {activeTab === "SPECS" && (
            <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom-4 duration-500">
            <div className="bg-white border p-4 shadow-sm rounded-sm flex flex-col md:flex-row justify-between items-end gap-4">
              <div className="flex-1 w-full"><EditableField label={t('tools.fields.class', 'Classification')} value={data.classstructureid_description || data.classstructureid} onSearch={() => setShowClassTree(true)} isRTL={isRTL} /></div>
              <button onClick={addNewSpecification} className="bg-[#3c70a4] text-white px-6 py-1.5 rounded-sm font-bold shadow hover:bg-[#1a3353] transition-all text-xs flex items-center gap-2 active:scale-95"><Plus size={14} /> {t('common.new_row', 'New Row')}</button>
            </div>
            <div className="bg-white border rounded shadow-sm overflow-hidden min-h-[400px]">
               <SpecsTable specs={data.specifications} selectedIdx={selectedSpecIdx} onSelect={setSelectedSpecIdx} isRTL={isRTL} t={t} />
               {selectedSpecIdx !== null && data.specifications?.[selectedSpecIdx] && (
                  <div className={`p-6 border-t bg-gray-50 grid grid-cols-2 gap-10 ${isRTL ? 'text-right' : 'text-left'}`}>
                      <EditableField label={t('tools.tables.attr', 'Attribute')} value={data.specifications[selectedSpecIdx].assetattrid} onSearch={() => handleSmartSearch('assetattrid', t('tools.lookup.attr', 'Attributes'), 'attributes')} isRTL={isRTL} />
                      <div className="flex flex-col gap-1 border-b border-gray-300">
                          <label className="text-gray-400 font-bold text-[9px] uppercase">{t('tools.tables.value', 'Value')}</label>
                          <input className={`outline-none py-1 bg-transparent font-bold text-blue-900 ${isRTL ? 'text-right' : 'text-left'}`} value={data.specifications[selectedSpecIdx].alnvalue || data.specifications[selectedSpecIdx].numvalue || ''} onChange={e => handleSpecUpdate('alnvalue', e.target.value)} />
                      </div>
                  </div>
               )}
            </div>
          </div>
        )}

        {activeTab === 'QUALS' && (
          <div className="max-w-6xl mx-auto bg-white border shadow-xl rounded-sm overflow-hidden animate-in slide-in-from-bottom">
             <div className="bg-gray-50 p-3 border-b flex justify-between px-8 items-center border-t-4 border-t-orange-600">
                <span className="font-black text-orange-900 tracking-widest italic text-[0.9em]">{t('tools.sections.quals', 'Required Qualifications')}</span>
                <button className="bg-orange-600 text-white px-4 py-1.5 rounded-sm font-black text-[0.9em] hover:bg-orange-800 flex items-center gap-2 active:scale-95">
                   <Plus size={12}/> {t('tools.actions.add_qual', 'Add Qualification')}
                </button>
             </div>
             <table className={`w-full font-bold uppercase ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-[#f9fafb] border-b text-gray-400 text-[0.9em]">
                  <tr>
                    <th className={`p-4 border-gray-100 ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.qual', 'Qualification')}</th>
                    <th className={`p-4 border-gray-100 ${isRTL ? 'border-l' : 'border-r'}`}>{t('assets.headers.desc', 'Description')}</th>
                    <th className="p-4">{t('common.org', 'Org ID')}</th>
                  </tr>
                </thead>
                <tbody className="text-[0.9em]">
                  {data.qualifications?.length > 0 ? data.qualifications.map((q, i) => (
                    <tr key={i} className="border-b hover:bg-orange-50 transition-colors">
                      <td className={`p-4 border-gray-50 text-blue-900 ${isRTL ? 'border-l' : 'border-r'}`}>{q.qualification}</td>
                      <td className={`p-4 border-gray-50 text-gray-500 ${isRTL ? 'border-l' : 'border-r'}`}>{q.description}</td>
                      <td className="p-4">{q.orgid}</td>
                    </tr>
                  )) : <tr><td colSpan="3" className="p-20 text-center text-gray-300 font-black italic uppercase tracking-widest">{t('common.no_records', 'No rows to display')}</td></tr>}
                </tbody>
             </table>
          </div>
        )}
      </div>

      {/* --- MODALS --- */}
      <UniversalLookup 
        {...lookup} 
        onClose={() => setLookup({...lookup, isOpen: false})} 
        onSelect={(row) => {
          if (lookup.onCustomSelect) { lookup.onCustomSelect(row); } 
          else {
            const val = row.assetnum || row.location_id || row.itemnum || row.meter_num || row.value || row.id || row.site_id;
            handleInputChange(lookup.field, val);
          }
          setLookup(prev => ({ ...prev, isOpen: false }));
        }} 
      />
      {isAddModalOpen && (
      <AddToolToStoreModal 
        isOpen={isAddModalOpen} 
        onClose={() => setIsAddModalOpen(false)} 
        toolnum={data.toolnum}
        onRefresh={() => {
            axios.get(`/api/tools/${data.toolnum}`).then(res => setData(res.data));
        }}
      />)}
      {showStatusModal && (
      <StatusModal isOpen={showStatusModal} currentStatus={data.status} recordId={data.toolnum} appName="TOOL" onClose={() => setShowStatusModal(false)} onComplete={loadData} />)}
      {isDocModalOpen && (
      <AttachmentsModal isOpen={isDocModalOpen} ownertable="TOOL" ownerid={data.toolnum} onClose={() => { setIsDocModalOpen(false); fetchCount(); }} />)}
      {showHistoryModal && (
      <StatusHistoryModal isOpen={showHistoryModal} appName="TOOL" recordId={data.toolnum} onClose={() => setShowHistoryModal(false)} />)}
      {showClassTree && (
      <ClassificationTree isOpen={showClassTree} onClose={() => setShowClassTree(false)} onSelect={handleClassSelect} />)}
    </div>
  );
};

// --- HELPERS ---
const SectionCard = ({ title, children }) => (
  <div className="bg-white border rounded shadow-sm overflow-hidden"><div className="p-2 bg-slate-50 border-b font-black text-slate-500 uppercase text-[0.9em] tracking-widest flex items-center gap-2 shadow-inner"><div className="w-1.5 h-3 bg-blue-600 rounded-full"></div> {title}</div><div className="p-8">{children}</div></div>
);

const StaticField = ({ label, value }) => (
  <div className="flex flex-col gap-1 border-b border-gray-100 pb-1 mb-4"><label className="text-gray-400 font-black text-[0.9em] uppercase tracking-widest">{label}</label><div className="font-bold text-gray-700 min-h-[15px]">{value || '---'}</div></div>
);

const CheckboxField = ({ label, checked, onChange, color="blue", isRTL }) => (
  <label className={`flex items-center gap-3 cursor-pointer group py-1 `}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className={`w-4 h-4 accent-${color}-600 rounded-sm cursor-pointer`} />
    <span className={`group-hover:text-${color}-600 transition-colors font-black text-gray-500 text-[0.9em] uppercase`}>{label}</span>
  </label>
);

const EditableField = ({ label, value, onChange, onSearch, isRTL }) => (
  <div className={`flex flex-col gap-1 border-b border-gray-300 focus-within:border-blue-600 transition-all uppercase mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-gray-400 font-black text-[0.9em] tracking-widest">{label}</label>
    <div className={`flex items-center justify-between py-1 `}>
        <input className={`font-black text-blue-900 outline-none bg-transparent flex-1 text-[0.9em] ${isRTL ? 'text-right' : 'text-left'}`} value={value || ''} onChange={e => onChange(e.target.value)} />
        {onSearch && <Search size={14} className="text-blue-600 cursor-pointer" onClick={onSearch} />}
    </div>
  </div>
);

const DataField = ({ label, value, onSearch, isRTL }) => (
  <div className={`flex flex-col border-b border-gray-100 py-1.5 group hover:bg-blue-50 transition-all rounded px-1 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
      <label className="text-[0.9em] font-black text-gray-400 uppercase">{label}</label>
      <div className={`flex justify-between items-center h-5 `}>
          <span className="font-black uppercase text-blue-900">{value || '---'}</span>
          {onSearch && <Search size={13} className="text-blue-400 cursor-pointer" onClick={onSearch} />}
      </div>
  </div>
);

const InventoryTable = ({ inventory = [], isRTL, t }) => (
  <table className={`w-full font-bold uppercase tracking-tighter ${isRTL ? 'text-right' : 'text-left'}`}>
    <thead className="bg-[#f9fafb] border-b text-gray-400 text-[0.9em]">
        <tr>
            <th className={`p-4 border-gray-100 ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.storeroom', 'Storeroom')}</th>
            <th className={`p-4 border-gray-100 ${isRTL ? 'border-l' : 'border-r'}`}>{t('common.site', 'Site')}</th>
            <th className={`p-4 border-gray-100 ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{t('tools.tables.balance', 'Balance')}</th>
            <th className="p-4 text-center">{t('assets.headers.status', 'Status')}</th>
        </tr>
    </thead>
    <tbody className="text-[0.9em]">
      {inventory.length > 0 ? inventory.map((item, idx) => (
        <tr key={idx} className="border-b hover:bg-blue-50 transition-colors font-black">
            <td className={`p-4 border-gray-50 text-blue-900 ${isRTL ? 'border-l' : 'border-r'}`}>{item.location}</td>
            <td className={`p-4 border-gray-50 text-gray-500 ${isRTL ? 'border-l' : 'border-r'}`}>{item.siteid}</td>
            <td className={`p-4 border-gray-50 text-green-700 font-mono ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{parseFloat(item.curbal || 0).toFixed(2)}</td>
            <td className="p-4 text-center">ACTIVE</td>
        </tr>
      )) : <tr><td colSpan="4" className="p-20 text-center text-gray-300 font-black italic uppercase tracking-widest">{t('tools.messages.no_stock', 'No items in stock')}</td></tr>}
    </tbody>
  </table>
);

const SpecsTable = ({ specs = [], selectedIdx, onSelect, isRTL, t }) => (
    <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
        <thead className="bg-gray-100 border-b text-gray-400 font-bold text-[0.9em] uppercase">
            <tr>
                <th className="p-3 w-10"></th>
                <th className={`p-3 border-gray-200 ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.attr', 'Attribute')}</th>
                <th className={`p-3 border-gray-200 ${isRTL ? 'border-l' : 'border-r'}`}>{t('assets.headers.desc', 'Description')}</th>
                <th className={`p-3 border-gray-200 ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.value', 'Value')}</th>
                <th className="p-3">{t('tools.tables.uom', 'UOM')}</th>
            </tr>
        </thead>
        <tbody>
            {specs.map((spec, i) => (
                <tr key={i} onClick={() => onSelect(i)} className={`border-b hover:bg-blue-50 cursor-pointer text-[11px] ${selectedIdx === i ? 'bg-[#e8f0fe]' : ''}`}>
                    <td className="p-3 text-center text-blue-600">{selectedIdx === i && <ChevronRight size={14} className={isRTL ? 'rotate-180 mx-auto' : 'mx-auto'}/>}</td>
                    <td className={`p-3 border-gray-50 font-bold text-blue-900 ${isRTL ? 'border-l' : 'border-r'}`}>{spec.assetattrid || '---'}</td>
                    <td className={`p-3 border-gray-50 text-gray-500 ${isRTL ? 'border-l' : 'border-r'}`}>{spec.description || '---'}</td>
                    <td className={`p-3 border-gray-50 font-bold text-green-700 ${isRTL ? 'border-l' : 'border-r'}`}>{spec.alnvalue || spec.numvalue || '---'}</td>
                    <td className="p-3 text-gray-400">{spec.uom || '---'}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

const VendorsTable = ({ vendors = [], isRTL, t }) => (
    <div className="flex flex-col animate-in fade-in duration-500">
        <div className={`bg-gray-50 p-3 border-b border-t-4 border-t-green-600 flex justify-between items-center px-8 shadow-sm `}>
            <span className={`text-[0.9em] font-black text-green-900 tracking-widest italic flex items-center gap-2 `}>
                <Box size={14} className="text-green-600"/> {t('tools.sections.vendors', 'PROCUREMENT & SUPPLIER HISTORY')}
            </span>
            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-[0.8em] font-black border border-green-200">
                {t('tools.fields.active_vendors', 'ACTIVE VENDORS')}: {vendors.length}
            </span>
        </div>
        <table className={`w-full border-collapse font-bold uppercase tracking-tighter ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-gray-100 border-b text-gray-400 text-[0.9em]">
                <tr>
                    <th className={`p-4 border-gray-200 w-48 ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.vendor_id', 'Vendor (Comp Num)')}</th>
                    <th className={`p-4 border-gray-200 ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.vendor_name', 'Supplier Name')}</th>
                    <th className={`p-4 border-gray-200 text-center w-32 ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.unitcost', 'Unit Cost')}</th>
                    <th className={`p-4 border-gray-200 text-center w-24 ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.qty', 'Qty')}</th>
                    <th className="p-4 w-32 text-center">{t('tools.tables.po', 'Reference PO')}</th>
                </tr>
            </thead>
            <tbody className="text-[0.9em]">
                {vendors.length > 0 ? vendors.map((v, i) => {
                    const cost = v.unitcost && !isNaN(v.unitcost) ? parseFloat(v.unitcost) : 0;
                    return (
                        <tr key={i} className="border-b hover:bg-green-50/50 transition-colors group">
                            <td className={`p-4 border-gray-50 text-blue-900 font-black ${isRTL ? 'border-l' : 'border-r'}`}>
                                {v.vendor_code || v.vendor || '---'}
                            </td>
                            <td className={`p-4 border-gray-50 text-gray-500 italic ${isRTL ? 'border-l' : 'border-r'}`}>
                                {v.vendor_name || t('tools.placeholders.reg_supplier', 'REGISTERED SUPPLIER')}
                            </td>
                            <td className={`p-4 border-gray-50 text-center font-mono text-green-700 ${isRTL ? 'border-l' : 'border-r'}`}>
                                {cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                <span className={`text-[0.8em] text-gray-400 font-sans ${isRTL ? 'mr-1' : 'ml-1'}`}>{v.currency || 'EGP'}</span>
                            </td>
                            <td className={`p-4 border-gray-50 text-center font-mono text-gray-700 ${isRTL ? 'border-l' : 'border-r'}`}>
                                {v.quantity || 0}
                            </td>
                            <td className="p-4 text-gray-400 font-medium text-center">
                                {v.ponum || t('tools.placeholders.adj', 'STOCK ADJ')}
                            </td>
                        </tr>
                    );
                }) : (
                    <tr>
                        <td colSpan="5" className="p-20 text-center text-gray-300 font-black italic tracking-widest">
                            {t('common.no_records', 'No Procurement Records Found')}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
);

export default ToolDetails;